import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Info,
  WandIcon,
  Table,
  LoadingComponent,
  StatsCard,
  MonthPicker,
  AsyncStatsCard,
} from '@demandlocal/components'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import { ReactComponent as GoogleIcon } from '../../../../images/icons/audience_google.svg'
import { ReactComponent as FacebookIcon } from '../../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from '../../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as BingIcon } from '../../../../images/icons/audience_bing.svg'
import { ReactComponent as ChevronLeft } from '../../../../images/icons/chevron_left.svg'
import { ReactComponent as ExpenseIcon } from '../../../../images/icons/expense_icon.svg'
import { ReactComponent as VehicleIcon } from '../../../../images/icons/vehicle.svg'
import { ReactComponent as VehiclePurchaseIcon } from '../../../../images/icons/summary_vehicles.svg'
import { ReactComponent as MultipleVehiclesIcon } from '../../../../images/icons/multiple_vehicles.svg'
import { ReactComponent as TimeToConvertIcon } from '../../../../images/icons/summary_time_to_convert.svg'
import { ReactComponent as SiteVisitorsIcon } from '../../../../images/icons/summary_site_visitors.svg'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { isEmpty, lowerCase, take, uniq } from 'lodash'
import { createColumnHelper, SortingState } from '@tanstack/react-table'
import { useGetPerformanceStats } from '../../../../api/dealership/useGetPerformanceStats'
import { DateRange } from '../../../../lib/types/DateRange'
import { Campaign } from '../../../../lib/types/Campaign'
import { useGetAttributedSales } from '../../../../api/dealership/inventory/useGetAttributedSales'
import { AttributedSoldVehicle } from '../../../../lib/types/Vehicle'
import { GA4 } from '../../../../lib/utils/ga4'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import utc from 'dayjs/plugin/utc'
import mtm from '../../../../lib/utils/mtm'
import { useGetCustomerSourcesByDealershipID } from '../../../../api/dealership/customers/sources/useGetCustomerSourcesByDealershipID'
import LineChart from '../../../../components/Charts/LineChart'
import { useGetCampaignsSummaryReport } from '../../../../api/dealership/campaigns/reports/useGetCampaignsSummaryReport'
import BarChart from '../../../../components/Charts/BarChart'
import PolarAreaChart from '../../../../components/Charts/PolarAreaChart'
import MonthlyComparisonPerformanceCard from '../../../../components/Overview/MonthlyComparisonPerformanceCard'
import { useGetUserRole } from '../../../../api/auth/useGetUserRole'
import { useDeleteAIInsights } from '../../../../api/dealership/campaigns/useDeleteAIInsights'
import { useGetCampaignsSummaryList } from '../../../../api/dealership/campaigns/useGetCampaignsSummaryList'
import { queryClient } from '../../../../api/queryClient'
import SoldVehiclePath from '../../../../components/Performance/SalesPaths/SoldVehiclePath'
dayjs.extend(advancedFormat)
dayjs.extend(utc)

const columnHelper = createColumnHelper<Campaign>()
const vehicleColumnHelper = createColumnHelper<AttributedSoldVehicle>()

const SummaryReport = (): JSX.Element => {
  const dealership = useDealership()
  const navigate = useNavigate()
  const { data: user } = useGetUserRole()
  const [queryParams, setQueryParams] = useSearchParams()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate:
        queryParams.get('startDate') ||
        dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate:
        queryParams.get('endDate') ||
        dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters.dateRange, setQueryParams])

  const { data: campaigns, isLoading: isLoadingCampaigns } = useGetCampaignsSummaryList({
    pageIndex: 1,
    pageSize: 20,
    filters: {
      ...filters?.dateRange,
      aiInsights: 1,
    },
    sorting: [],
  })

  const {
    data: campaignReport,
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReport,
  } = useGetCampaignsSummaryReport({
    filters: { ...filters.dateRange },
  })

  const { data: inventoryPerformance, isLoading } = useGetPerformanceStats({
    filters: {
      start_date: filters.dateRange?.startDate,
      end_date: filters.dateRange?.endDate,
    },
  })

  const { data: previousMonthStats, isLoading: isLoadingPreviousMonth } =
    useGetPerformanceStats({
      filters: {
        start_date: dayjs(filters.dateRange.startDate)
          .subtract(1, 'M')
          .startOf('M')
          .format('YYYY-MM-DD'),
        end_date: dayjs(filters.dateRange.endDate)
          .subtract(1, 'M')
          .endOf('M')
          .format('YYYY-MM-DD'),
      },
    })

  const {
    data: attributedSales,
    isLoading: isLoadingSales,
    isPreviousData,
  } = useGetAttributedSales({
    pageSize: 9999,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const { data: sources, isLoading: isLoadingSources } =
    useGetCustomerSourcesByDealershipID({
      pageIndex: 1,
      pageSize: 100,
      sorting: [{ id: 'updated_date', sort: 'desc' }],
    })

  const dmsSource = useMemo(() => {
    const s = sources?.data?.filter(
      (s) => s.data_provider !== 'Manual Upload' && s.customer_source_type === 'Sales',
    )
    return s && s?.length > 0 ? s[0] : null
  }, [sources])

  const showAIInsights = useMemo(() => {
    return !!campaigns?.insights?.data && dealership?.enable_ai_insights
  }, [campaigns?.insights?.data, dealership?.enable_ai_insights])

  const hasAttributedSales = useMemo(() => {
    return !isLoadingSales && !isLoadingSources && !isEmpty(attributedSales?.data)
  }, [attributedSales?.data, isLoadingSales, isLoadingSources])

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const isLoadingAll = useMemo(() => {
    return (
      isLoading ||
      isLoadingCampaigns ||
      isLoadingPreviousMonth ||
      isLoadingReport ||
      isLoadingSales ||
      isLoadingSources
    )
  }, [
    isLoading,
    isLoadingCampaigns,
    isLoadingPreviousMonth,
    isLoadingReport,
    isLoadingSales,
    isLoadingSources,
  ])

  useEffect(() => {
    if (!isLoadingAll) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingAll])

  const dailyAttributedSales = useMemo(() => {
    return attributedSales?.data
      ?.map((sale) => {
        return {
          date: sale.sold_date,
          month: dayjs.utc(sale.sold_date).format('MMMM'),
          day: dayjs.utc(sale.sold_date).format('D'),
          vin_number: sale.vin_number,
        }
      })
      ?.reduce((acc, cur) => {
        const date = dayjs.utc(cur.date).format('YYYY-MM-DD')
        const existingDate = acc.find((item) => item.date === date)
        if (existingDate) {
          existingDate.count++
        } else {
          acc.push({
            month: cur.month,
            day: cur.day,
            date: date,
            count: 1,
          })
        }
        return acc
      }, [] as { date: string; month: string; day: string; count: number }[])
      .sort((a, b) => (dayjs(a.date).isAfter(b.date) ? 1 : -1))
  }, [attributedSales])

  const attributedSalesColumns = useMemo(
    () => [
      vehicleColumnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              className={'font-bold text-xs text-primary-persian-blue'}
              {...{
                onClick: (event) => {
                  event.preventDefault()
                  row.toggleExpanded()
                },
                style: { cursor: 'pointer' },
              }}>
              {row.original.vin_number}
            </button>
          ) : (
            row.original.vin_number
          )
        },
      }),
      vehicleColumnHelper.accessor('sold_date', {
        header: 'Date Sold',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
        enableSorting: false,
      }),
      vehicleColumnHelper.display({
        id: 'vehicle',
        header: 'Vehicle',
        cell: ({ row }) =>
          !row.original.year && !row.original.make && !row.original.model
            ? 'Unknown'
            : `${row.original.year ? row.original.year : ''} ${
                row.original.make ? row.original.make : ''
              } ${row.original.model ? row.original.model : ''}`,
        enableSorting: false,
      }),
      vehicleColumnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => `$${formatNumber(Number(info.getValue()))}`,
        enableSorting: false,
      }),
      vehicleColumnHelper.accessor('time_to_convert', {
        header: () => (
          <div className={'flex flex-row gap-2 items-center justify-between'}>
            <p>Days to Purchase</p>
            <Info message={'Days between first impression and purchase'} />
          </div>
        ),
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      vehicleColumnHelper.accessor('days_on_lot', {
        header: () => (
          <div className={'flex flex-row gap-2 items-center justify-between'}>
            <p>Days on Lot</p>
            <Info message={'Days vehicle seen at dealership'} />
          </div>
        ),
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      vehicleColumnHelper.display({
        id: 'vendors',
        header: 'Marketed On',
        cell: ({ row }) => {
          return (
            <div className={'flex flex-row gap-2'}>
              {uniq(
                row.original.firstImpressionDetails?.map(
                  (impression) => impression.vendor,
                ),
              )?.map((vendor) => {
                return (
                  <div key={vendor}>
                    {vendor === 'Google' && <GoogleIcon />}
                    {vendor === 'Facebook' && <FacebookIcon />}
                    {(vendor === 'Advanse' ||
                      vendor === 'Celtra' ||
                      vendor === 'Trade Desk') && <TradeDeskIcon />}
                    {vendor === 'Bing' && <BingIcon />}
                  </div>
                )
              })}
            </div>
          )
        },
      }),
    ],
    [],
  )

  const campaignColumns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('client_campaign_display_name', {
        header: 'Name',
        cell: (info) =>
          info.row.original.campaign_status === 'pending' ? (
            <p className={'text-primary-persian-blue font-bold'}>
              {info.getValue() ?? '-'}
            </p>
          ) : (
            <Link
              className={'text-primary-persian-blue font-bold'}
              to={`../${lowerCase(info.row.original.product_code)}/${
                info.row.original.client_campaign_id
              }/?startDate=${dayjs(info.row.original.start_date).format(
                'YYYY-MM-DD',
              )}&endDate=${dayjs(info.row.original.end_date).format('YYYY-MM-DD')}`}>
              {info.getValue() || '-'}
            </Link>
          ),
        footer: ({ table }) =>
          `Total for ${table.getFilteredRowModel().rows.length} campaigns`,
      }),
      columnHelper.accessor('start_date', {
        header: 'Start Date',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      columnHelper.accessor('end_date', {
        header: 'End Date',
        cell: (info) => dayjs(info.getValue()).format('MMM D, YYYY'),
      }),
      columnHelper.display({
        header: 'Product',
        id: 'product',
        cell: ({ row }) =>
          row.original.campaign_type === 'audience'
            ? `${row.original.product_name}`
            : `${row.original.product_name} (${row.original.product_code})`,
      }),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows?.reduce(
                (total, row) => total + Number(row.getValue('impressions')),
                0,
              ),
          ),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows?.reduce((total, row) => total + Number(row.getValue('clicks')), 0),
          ),
      }),
    ]

    return hasAttributedSales
      ? [
          ...baseColumns,
          columnHelper.accessor('sales', {
            header: () => (
              <div className={'flex flex-row gap-2 items-center justify-between'}>
                <p>Attributed sales</p>
                <Info message={'Marketed vehicles that were purchased'} />
              </div>
            ),
            cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
          }),
        ]
      : baseColumns
  }, [hasAttributedSales])

  const { mutateAsync: deleteInsights } = useDeleteAIInsights()

  if (isLoadingReport) return <LoadingComponent title="Loading..." />

  return (
    <div className={'px-12 pb-10'} id={'content'}>
      <div className={'mb-7'}>
        <div className={'flex justify-between top-0 py-6 z-10'}>
          <div
            className={'flex flex-1 max-w-[1920px] mx-auto justify-between items-center'}>
            <div className={`flex flex-1 mx-auto flex-row justify-between items-center`}>
              <div className={'flex flex-row items-center gap-3'}>
                <ChevronLeft
                  onClick={() => navigate(-1)}
                  className={'fill-primary-xiketic/50 cursor-pointer'}
                />
                <h1 className={'text-3xl font-Spartan font-bold text-primary-xiketic'}>
                  {`${dealership?.dealership} - ${dayjs(
                    filters.dateRange.startDate,
                  ).format('MMMM YYYY')}`}
                </h1>
              </div>
            </div>
            <div className={'flex flex-row gap-4 items-center print:hidden'}>
              {user?.data?.role_id === 1 && showAIInsights && (
                <Button
                  onClick={async () => {
                    const toastId = toast.loading('Deleting insights...')
                    await deleteInsights()
                      .then(async (res) => {
                        toast.success(
                          'Deleted! Standby as new insights are being generated',
                          { id: toastId },
                        )
                        await queryClient.refetchQueries([
                          'dealership',
                          dealership?.id,
                          'campaigns',
                          'month-over-month',
                          {
                            pageIndex: undefined,
                            pageSize: undefined,
                            queryParams: {
                              aiInsights: 1,
                              ...filters.dateRange,
                            },
                            sortingParams: undefined,
                          },
                        ])
                      })
                      .catch((err) => {
                        toast.error('An error has occured', { id: toastId })
                      })
                  }}>
                  Regenerate AI insights
                </Button>
              )}
              <MonthPicker
                defaultDate={dayjs().subtract(1, 'M').startOf('M').toDate()}
                maxDate={dayjs().startOf('M').subtract(1, 'M').toDate()}
                onChange={(e) => {
                  setFilters({
                    dateRange: {
                      startDate: dayjs(e).startOf('M').format('YYYY-MM-DD'),
                      endDate: dayjs(e).endOf('M').format('YYYY-MM-DD'),
                    },
                  })
                }}
              />
              <Button
                variant={'rainbow'}
                disabled={isLoading || isDownloadingReport}
                leftSlot={<DownloadIcon className={'fill-white'} />}
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Generating report...')
                  await downloadReport(undefined, {
                    async onSuccess(data) {
                      toast.success('Report generated successfully', { id: toastId })
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${dealership?.dealership.replaceAll(
                        ' ',
                        '_',
                      )}_campaigns-overview_${dayjs(filters.dateRange.startDate).format(
                        'MMM-YYYY',
                      )}.pdf`
                      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                      a.click()
                      a.remove()
                    },
                  })
                  GA4.sendEvent({
                    event_name: 'export_pdf',
                    event_params: {
                      campaign_type: 'campaigns_summary',
                      campaign_id: dealership?.id,
                    },
                  })
                  mtm.sendEvent('Export', 'PDF', 'campaigns_summary', dealership?.id)
                }}>
                Export as PDF
              </Button>
            </div>
          </div>
        </div>
        <div className={'flex flex-col gap-5'}>
          <div
            key={'customers-stats-card-expanded'}
            className={'max-w-[1920px] mx-auto w-full'}>
            <div className={`grid grid-cols-3 gap-5`}>
              <MonthlyComparisonPerformanceCard
                title={'Units Sold'}
                color={'blue'}
                icon={<MultipleVehiclesIcon />}
                leftSlot={{
                  label: dayjs(filters.dateRange.startDate)
                    .subtract(1, 'M')
                    .format("MMMM 'YY"),
                  value: previousMonthStats?.unitsSold ?? 0,
                }}
                rightSlot={{
                  label: dayjs(filters.dateRange.startDate).format("MMMM 'YY"),
                  value: inventoryPerformance?.unitsSold ?? 0,
                }}
                isLoading={isLoading || isLoadingPreviousMonth}
              />
              <MonthlyComparisonPerformanceCard
                title={'Influenced Sales'}
                color={'purple'}
                icon={<VehicleIcon />}
                leftSlot={{
                  label: dayjs(filters.dateRange.startDate)
                    .subtract(1, 'M')
                    .format("MMMM 'YY"),
                  value: previousMonthStats?.attributedSales ?? 0,
                }}
                rightSlot={{
                  label: dayjs(filters.dateRange.startDate).format("MMMM 'YY"),
                  value: inventoryPerformance?.attributedSales ?? 0,
                }}
                isLoading={isLoading || isLoadingPreviousMonth}
              />
              <MonthlyComparisonPerformanceCard
                title={'Revenue from Influenced Sales'}
                color={'yellow'}
                icon={<ExpenseIcon />}
                leftSlot={{
                  label: dayjs(filters.dateRange.startDate)
                    .subtract(1, 'M')
                    .format("MMMM 'YY"),
                  value: previousMonthStats?.attributedRevenue ?? 0,
                }}
                rightSlot={{
                  label: dayjs(filters.dateRange.startDate).format("MMMM 'YY"),
                  value: inventoryPerformance?.attributedRevenue ?? 0,
                }}
                isCurrency
                isLoading={isLoading || isLoadingPreviousMonth}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={'flex flex-col gap-10'}>
        {!isEmpty(campaignReport?.conversions) && (
          <div className={'flex grid grid-cols-3 gap-5'}>
            <span className={'rounded-xl'}>
              <AsyncStatsCard
                title={'Average Days to Purchase Vehicle'}
                content={`${formatNumber(
                  Math.round(
                    Math.min(
                      inventoryPerformance?.daysToConvert || Infinity,
                      previousMonthStats?.daysToConvert || Infinity,
                    ) || 0,
                  ),
                )} days`}
                color={'bg-chrysler-blue/20'}
                border={false}
                rightSlot={<VehiclePurchaseIcon />}
                isLoading={isLoading || isLoadingPreviousMonth}
              />
            </span>

            <span className={'rounded-xl'}>
              <AsyncStatsCard
                title={'Average Time to Convert'}
                content={campaignReport?.timeToConversion ?? 'N/A'}
                color={'bg-light-cyan'}
                border={false}
                rightSlot={<TimeToConvertIcon />}
                isLoading={isLoadingReport}
              />
            </span>
            <span className={'rounded-xl'}>
              <AsyncStatsCard
                title={'Unique Site Visitors'}
                content={formatNumber(
                  campaignReport?.uniqueSiteVisitors?.reduce(
                    (acc, cur) => acc + cur.last_campaign_uniques,
                    0,
                  ) || 0,
                )}
                color={'bg-champagne-pink'}
                border={false}
                rightSlot={<SiteVisitorsIcon />}
                isLoading={isLoadingReport}
              />
            </span>
          </div>
        )}
        {showAIInsights && (
          <div
            className={`grid grid-cols-${Math.min(
              campaigns?.insights?.data.length || 0,
              3,
            )} gap-5`}>
            {take(
              campaigns?.insights?.data?.map((data) => {
                return (
                  <div
                    key={data.title}
                    className={
                      'flex flex-row gap-3 items-center bg-cyan-100 py-5 px-3 rounded-lg text-sm'
                    }>
                    <span className={'px-3'}>
                      <WandIcon />
                    </span>
                    <div className={'flex flex-col gap-2'}>
                      <span className={'font-bold text-lg'}>{data.title}</span>
                      <span className={'text-md'}>{data.message}</span>
                    </div>
                  </div>
                )
              }),
              3,
            )}
          </div>
        )}
        <div className={'flex flex-col gap-6'}>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Impressions</span>
                <span>
                  {!isLoadingReport &&
                    !isLoadingCampaigns &&
                    formatNumber(
                      campaignReport?.metrics
                        ?.map((data) => data.impressions)
                        ?.reduce((acc, cur) => acc + cur, 0) || 0,
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={campaignReport?.metrics ?? []}
                  gradient={'green-to-cyan'}
                  yKey={'impressions'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReport}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Clicks</span>
                <span>
                  {!isLoadingReport &&
                    formatNumber(
                      campaignReport?.metrics
                        ?.map((data) => data.clicks)
                        ?.reduce((acc, cur) => acc + cur, 0) || 0,
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={campaignReport?.metrics ?? []}
                  gradient={'red-to-orange'}
                  yKey={'clicks'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReport}
                />
              </div>
            </div>
          </div>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impressions by Day of Week</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <BarChart
                  data={
                    campaignReport?.dayOfWeekData
                      ?.map(({ day_of_week, impressions }) => {
                        return {
                          label: day_of_week,
                          value: impressions,
                        }
                      })
                      ?.sort((a, b) => (dayjs(a.label) > dayjs(b.label) ? 1 : -1)) ?? []
                  }
                  customOptions={{
                    legend: {
                      display: false,
                    },
                  }}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReport}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic'}>
                <span>CTR</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={campaignReport?.metrics ?? []}
                  gradient={'blue-to-cyan'}
                  yKey={'ctr'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReport}
                />
              </div>
            </div>
          </div>
          {!isEmpty(campaignReport?.conversions) && (
            <div className="flex flex-col gap-8">
              <div className={'flex grid grid-cols-3 gap-5'}>
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>Attributed Site Visits</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      data={
                        campaignReport?.conversions
                          ?.filter((d) => d.id > 0 && d.conversions > 0)
                          ?.map((data) => {
                            return {
                              label: data.tag_name,
                              value: data.conversions,
                            }
                          })
                          ?.sort((a, b) => (a.value > b.value ? -1 : 1)) ?? []
                      }
                      isPreviousData={isPreviousData}
                      isLoading={isLoadingReport}
                    />
                  </div>
                </div>
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>Site Visitors by First Impression Campaign</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      data={
                        take(
                          campaignReport?.uniqueSiteVisitors
                            ?.map((data) => {
                              return {
                                label: data.campaign,
                                value: data.first_campaign_uniques,
                              }
                            })
                            ?.filter((d) => d.value > 0),
                          5,
                        ) ?? []
                      }
                      isPreviousData={isPreviousData}
                      isLoading={isLoadingReport}
                    />
                  </div>
                </div>
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>Site Visitors by Last Impression campaign</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      data={
                        take(
                          campaignReport?.uniqueSiteVisitors
                            ?.map((data) => {
                              return {
                                label: data.campaign,
                                value: data.last_campaign_uniques,
                              }
                            })
                            ?.filter((d) => d.value > 0),
                          5,
                        ) ?? []
                      }
                      isPreviousData={isPreviousData}
                      isLoading={isLoadingReport}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <span className={'font-bold text-primary-xiketic text-sm'}>Campaigns</span>
          <Table
            hidePagination={true}
            emptyLabel={'No campaigns found'}
            columns={campaignColumns}
            filters={{ ...filters.dateRange, aiInsights: 1 }}
            fetchFn={useGetCampaignsSummaryList}
            isDownloadable={true}
          />
          {hasAttributedSales && (
            <div className={'flex grid grid-cols-1 gap-5 mt-3'}>
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={`pt-5 pb-2 px-5 flex justify-between font-bold text-sm items-center text-primary-xiketic ${
                    dailyAttributedSales?.length === 0 && 'opacity-50'
                  }`}>
                  <span>Influenced sales</span>
                  <div className={'flex flex-row gap-2 items-center'}>
                    <span>
                      {!isLoading &&
                        new Intl.NumberFormat('en-US').format(
                          inventoryPerformance?.attributedSales ?? 0,
                        )}
                    </span>
                  </div>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    customOptions={{
                      scales: {
                        y: {
                          min: 0,
                          ticks: {
                            stepSize: 1,
                          },
                        },
                      },
                    }}
                    data={dailyAttributedSales ?? []}
                    gradient={'blue-to-purple'}
                    yKey={'count'}
                    isLoading={isLoadingSales}
                    isPreviousData={isPreviousData}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {hasAttributedSales && (
          <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-row items-center gap-2'}>
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Influenced sales
              </span>
              {dmsSource && (
                <Info message={`Sales loaded through ${dmsSource?.customer_source}`} />
              )}
            </div>
            <Table
              emptyLabel={'No vehicles found'}
              columns={attributedSalesColumns}
              defaultSorting={[{ id: 'sold_date', desc: true }]}
              filters={{
                start_date: filters.dateRange.startDate,
                end_date: filters.dateRange.endDate,
              }}
              fetchFn={useGetAttributedSales}
              getRowCanExpand={() => true}
              renderSubComponent={SoldVehiclePath}
              customPageSize={999}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default SummaryReport
